import { useEffect, useState, useCallback } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { TemplateList } from "@/components/admin/TemplateList";
import { TemplateEditor } from "@/components/admin/TemplateEditor";
import { CreateTemplateModal } from "@/components/admin/CreateTemplateModal";
import {
  useTemplateList,
  useTemplateDetail,
  useUpdateTemplate,
  useCreateTemplate,
  type TemplateListItem,
  type SectionConfig,
} from "@/hooks/use-admin-templates";
import {
  useBodyRegionStructures,
  useAssignedStructures,
  useStructureSearch,
} from "@/hooks/use-body-region-structures";
import {
  useCustomSections,
  useCustomStructures,
} from "@/hooks/use-custom-sections-structures";
import { useCombinedSectionPool, type CombinedSectionItem } from "@/hooks/use-combined-section-pool";
import { useDebouncedCallback } from "use-debounce";

export default function Templates() {
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateListItem | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Hooks
  const { grouped, loading: listLoading, fetch: fetchList } = useTemplateList();
  const { detail, loading: detailLoading, fetch: fetchDetail, clear: clearDetail } = useTemplateDetail();
  const {
    updating,
    updateExam,
    updateTechnique,
    updateSection,
    updateSectionTitle,
    addSection,
    removeSection,
    reorderSections,
    toggleStructure,
    updateEnabledSections,
    restoreDefaultSections,
    syncDefaultSections,
  } = useUpdateTemplate();
  const { creating, create } = useCreateTemplate();
  const { sections: combinedSections, loading: combinedSectionsLoading, fetch: fetchCombinedSections } = useCombinedSectionPool();

  // Structure hooks
  const { structures: bodyRegionStructures, loading: bodyRegionLoading, fetch: fetchBodyRegionStructures } = useBodyRegionStructures();
  const { structures: assignedStructures, loading: assignedLoading, fetch: fetchAssignedStructures } = useAssignedStructures();
  const { results: structureSearchResults, loading: structureSearchLoading, search: searchStructures, clear: clearStructureSearch } = useStructureSearch();

  // Custom section/structure hooks
  const { create: createCustomSection } = useCustomSections();
  const { create: createCustomStructure } = useCustomStructures();

  // Initial fetch
  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Fetch detail when template selected
  useEffect(() => {
    if (selectedTemplate) {
      fetchDetail(selectedTemplate.exam_family_id);
    } else {
      clearDetail();
    }
  }, [selectedTemplate, fetchDetail, clearDetail]);

  // Fetch combined section pool when detail is loaded
  useEffect(() => {
    if (detail?.exam.modality && detail?.exam.body_region) {
      fetchCombinedSections(detail.exam.modality, detail.exam.body_region, detail.exam.display_name);
    }
  }, [detail?.exam.modality, detail?.exam.body_region, detail?.exam.display_name, fetchCombinedSections]);

  // Fetch structures when detail is loaded
  useEffect(() => {
    if (detail?.exam.exam_family_id) {
      fetchAssignedStructures(detail.exam.exam_family_id);
    }
  }, [detail?.exam.exam_family_id, fetchAssignedStructures]);

  // Fetch body region structures when body_region changes
  useEffect(() => {
    if (detail?.exam.body_region) {
      // Convert body_region to body_region_structure_pool format
      // e.g., "CHEST" -> try "CHEST_LUNGS", "CHEST_MEDIASTINUM", etc.
      // For now, fetch using the modality + body_region pattern
      const bodyRegion = `${detail.exam.body_region.toUpperCase()}`;
      fetchBodyRegionStructures(bodyRegion);
    }
  }, [detail?.exam.body_region, fetchBodyRegionStructures]);

  // Debounced search
  const debouncedSearch = useDebouncedCallback((search: string) => {
    fetchList(search || undefined);
  }, 300);

  // Handlers
  const handleSelectTemplate = (template: TemplateListItem) => {
    setSelectedTemplate(template);
  };

  const handleSearch = (search: string) => {
    debouncedSearch(search);
  };

  const handleCreateNew = () => {
    setCreateModalOpen(true);
  };

  const handleUpdateExam = useCallback(async (displayName: string, isActive: boolean): Promise<boolean> => {
    if (!detail) return false;
    const success = await updateExam(detail.exam.exam_family_id, displayName, isActive);
    if (success) {
      fetchList();
    }
    return success;
  }, [detail, updateExam, fetchList]);

  const handleUpdateTechnique = useCallback(async (techniqueText: string): Promise<boolean> => {
    if (!detail) return false;
    return await updateTechnique(detail.exam.exam_family_id, techniqueText);
  }, [detail, updateTechnique]);

  // --- SectionWorkspace handlers ---

  const handleUpdateSectionTitle = useCallback(async (sectionToken: string, title: string): Promise<boolean> => {
    if (!detail) return false;
    const examFamilyId = detail.exam.exam_family_id;
    const success = await updateSectionTitle(examFamilyId, sectionToken, title);
    if (success) {
      await syncDefaultSections(examFamilyId);
      fetchDetail(examFamilyId);
    }
    return success;
  }, [detail, updateSectionTitle, syncDefaultSections, fetchDetail]);

  const handleUpdateDefaultText = useCallback(async (sectionToken: string, defaultText: string): Promise<boolean> => {
    if (!detail) return false;
    const success = await updateSection(detail.exam.exam_family_id, sectionToken, defaultText);
    if (success) {
      fetchDetail(detail.exam.exam_family_id);
    }
    return success;
  }, [detail, updateSection, fetchDetail]);

  const handleDeleteSection = useCallback(async (sectionToken: string): Promise<boolean> => {
    if (!detail) return false;
    const examFamilyId = detail.exam.exam_family_id;
    const success = await removeSection(examFamilyId, sectionToken);
    if (success) {
      await syncDefaultSections(examFamilyId);
      // If custom_sections was set, also remove the token from it
      if (detail.exam.custom_sections) {
        const updated = detail.exam.custom_sections.filter(s => s.section_token !== sectionToken);
        await updateEnabledSections(examFamilyId, updated);
      }
      fetchDetail(examFamilyId);
    }
    return success;
  }, [detail, removeSection, syncDefaultSections, updateEnabledSections, fetchDetail]);

  const handleAddFromPool = useCallback(async (section: CombinedSectionItem): Promise<boolean> => {
    if (!detail) return false;
    const examFamilyId = detail.exam.exam_family_id;

    // Convert CombinedSectionItem to SectionConfig (drop source property)
    const sectionConfig: SectionConfig = {
      section_token: section.section_token,
      section_title: section.section_title,
      order_index: section.order_index,
    };

    // Add to template_section_registry
    const existingTokens = new Set(detail.sections.map(s => s.section_token));
    if (!existingTokens.has(section.section_token)) {
      const success = await addSection(examFamilyId, section.section_token, section.section_title, section.order_index);
      if (!success) return false;
    }

    // Sync default_sections
    await syncDefaultSections(examFamilyId);

    // If custom_sections was set, also add the token to it
    if (detail.exam.custom_sections) {
      const updated = [...detail.exam.custom_sections, sectionConfig].sort((a, b) => a.order_index - b.order_index);
      await updateEnabledSections(examFamilyId, updated);
    }

    fetchDetail(examFamilyId);
    return true;
  }, [detail, addSection, syncDefaultSections, updateEnabledSections, fetchDetail]);

  const handleReorderSections = useCallback(async (orderedTokens: string[]): Promise<boolean> => {
    if (!detail) return false;
    const examFamilyId = detail.exam.exam_family_id;
    const success = await reorderSections(examFamilyId, orderedTokens);
    if (success) {
      await syncDefaultSections(examFamilyId);
      fetchDetail(examFamilyId);
    }
    return success;
  }, [detail, reorderSections, syncDefaultSections, fetchDetail]);

  const handleToggleSectionEnabled = useCallback(async (sectionToken: string, enabled: boolean): Promise<boolean> => {
    if (!detail) return false;
    const examFamilyId = detail.exam.exam_family_id;

    // Compute updated custom_sections
    const currentEnabled = detail.exam.custom_sections ?? detail.exam.default_sections ?? [];
    let newSections: SectionConfig[];

    if (enabled) {
      // Find section info from the registry
      const sectionInfo = detail.sections.find(s => s.section_token === sectionToken);
      if (!sectionInfo) return false;
      newSections = [...currentEnabled, {
        section_token: sectionInfo.section_token,
        section_title: sectionInfo.section_title,
        order_index: sectionInfo.order_index,
      }].sort((a, b) => a.order_index - b.order_index);
    } else {
      newSections = currentEnabled.filter(s => s.section_token !== sectionToken);
    }

    const success = await updateEnabledSections(examFamilyId, newSections);
    if (success) {
      fetchDetail(examFamilyId);
    }
    return success;
  }, [detail, updateEnabledSections, fetchDetail]);

  const handleRestoreDefaultSections = useCallback(async (): Promise<boolean> => {
    if (!detail) return false;
    const examFamilyId = detail.exam.exam_family_id;
    const success = await restoreDefaultSections(examFamilyId);
    if (success) {
      fetchDetail(examFamilyId);
    }
    return success;
  }, [detail, restoreDefaultSections, fetchDetail]);

  // Structure handlers
  const handleToggleStructure = useCallback(async (sectionToken: string, structureKey: string, include: boolean): Promise<boolean> => {
    if (!detail) return false;
    const examFamilyId = detail.exam.exam_family_id;
    const success = await toggleStructure(examFamilyId, sectionToken, structureKey, include);
    if (success) {
      fetchAssignedStructures(examFamilyId);
    }
    return success;
  }, [detail, toggleStructure, fetchAssignedStructures]);

  const debouncedStructureSearch = useDebouncedCallback((query: string) => {
    searchStructures(query);
  }, 300);

  const handleStructureSearch = useCallback((query: string) => {
    debouncedStructureSearch(query);
  }, [debouncedStructureSearch]);

  const handleClearStructureSearch = useCallback(() => {
    clearStructureSearch();
  }, [clearStructureSearch]);

  const handleCreate = useCallback(async (
    examFamilyId: string,
    displayName: string,
    modality: string,
    bodyRegion: string,
    contrastPhase: string
  ): Promise<boolean> => {
    const success = await create(examFamilyId, displayName, modality, bodyRegion, contrastPhase);
    if (success) {
      await fetchList();
      setSelectedTemplate({
        exam_family_id: examFamilyId,
        display_name: displayName,
        modality,
        body_region: bodyRegion,
        is_active: true,
      });
    }
    return success;
  }, [create, fetchList]);

  return (
    <AdminLayout
      title="Template Editor"
      description="Manage exam templates, sections, and structures"
    >
      <div className="flex h-[calc(100vh-200px)] -mx-6 -mb-6 border-t">
        {/* Left Panel - Template List */}
        <div className={sidebarCollapsed ? "w-14 flex-shrink-0 transition-all duration-200" : "w-80 flex-shrink-0 transition-all duration-200"}>
          <TemplateList
            grouped={grouped}
            loading={listLoading}
            selectedId={selectedTemplate?.exam_family_id || null}
            collapsed={sidebarCollapsed}
            onSelect={handleSelectTemplate}
            onSearch={handleSearch}
            onCreateNew={handleCreateNew}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        </div>

        {/* Right Panel - Template Editor */}
        <div className="flex-1 bg-background">
          <TemplateEditor
            detail={detail}
            loading={detailLoading}
            updating={updating}
            combinedSections={combinedSections}
            combinedSectionsLoading={combinedSectionsLoading}
            onUpdateExam={handleUpdateExam}
            onUpdateTechnique={handleUpdateTechnique}
            onUpdateSectionTitle={handleUpdateSectionTitle}
            onUpdateDefaultText={handleUpdateDefaultText}
            onDeleteSection={handleDeleteSection}
            onAddFromPool={handleAddFromPool}
            onReorderSections={handleReorderSections}
            onToggleSectionEnabled={handleToggleSectionEnabled}
            onRestoreDefaultSections={handleRestoreDefaultSections}
            // Structure props
            assignedStructures={assignedStructures}
            availableStructures={bodyRegionStructures}
            structureSearchResults={structureSearchResults}
            structuresLoading={bodyRegionLoading || assignedLoading}
            structureSearchLoading={structureSearchLoading}
            onToggleStructure={handleToggleStructure}
            onStructureSearch={handleStructureSearch}
            onClearStructureSearch={handleClearStructureSearch}
            // Custom creation props
            onCreateCustomSection={createCustomSection}
            onCreateCustomStructure={createCustomStructure}
          />
        </div>
      </div>

      {/* Create Template Modal */}
      <CreateTemplateModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onCreate={handleCreate}
        creating={creating}
      />
    </AdminLayout>
  );
}
